
import { defineComponent, ref, getCurrentInstance, onMounted, onActivated, onDeactivated } from 'vue'
import cookies from 'js-cookie';
import PictureCard from './components/picture-card/index.vue'
import TYMLCard from './components/tyml-card/index.vue'
import VideoCard from './components/video-card/index.vue'
import Load from './components/load/index.vue'
import PictureWaterfall from './components/water-fall/index.vue'
import { rafThrottle } from './components/utils'
import { extractTitleFromMetaInfo, getDefaultTitle, getLdJson, getMetaInfo, header } from '@/common/seo'
import { setCommonParamsCookiesInServer } from '@/common/common-params'
import { commonPvParams } from '@/common/utils'
import SearchBar from '@/components/search-bar/index.vue'
import { immediateSendLog, getKwaiLog } from '@/common/radar';
import { Picture, TopicApi, TopicApiService, TopicItem } from '~/api/topic';
import { Feed } from '~/api/feed';
import { ResponseBaseResult, WebFeedType } from '~/api/base';

export default defineComponent<any>({
  components: {
    PictureWaterfall,
    PictureCard,
    SearchBar,
    Load,
    TYMLCard,
    VideoCard
  },
  setup(props, ctx) {
    const fmp_time = Date.now();
    const { proxy } = getCurrentInstance() as { proxy: Record<string, any> }
    const isLoading = ref(false)
    const cursorIndex = ref('')
    const pictureList = ref<any>([])
    const showEmptyImg = ref(false)
    const topicNameContent = ref('')
    // 获取图片列表
    const getData = async (firstRequest = false) => {
      if (isLoading.value || cursorIndex.value === 'no_more') {
        return
      }

      isLoading.value = true
      try {
        const response = await TopicApi(proxy?.$axios).getTopicList({
          topic: encodeURIComponent(topicNameContent.value?.trim()?.replace(/\s/g, '-')),
          pcursor: cursorIndex.value,
          count: 25,
        })

        const { result, feeds = [], pcursor = '' } = response || {}

        if (result === ResponseBaseResult.RESULT_OK) {
          if (feeds.length) {
            pictureList.value.push(...feeds)
            cursorIndex.value = pcursor
          } else {
            cursorIndex.value = 'no_more'
          }
          showEmptyImg.value = firstRequest && feeds.length === 0
        }
      } catch(error) {
        if (pictureList.value.length === 0) {
          showEmptyImg.value = true
        }
      } finally {
        isLoading.value = false
      }
    }

    onMounted(() => {
      const webLogger = getKwaiLog();
      try {
        webLogger?.plugins?.radar?.fmp(fmp_time);
      } catch (error) {
        console.error(error);
      }

      if (proxy?.$route.params) {
        proxy.$store.commit('setTopicName', proxy.$route.params?.content?.replace(/(-)/g, ' '));
      }
      topicNameContent.value = proxy?.$store?.state?.topicName;
      sendPv()

      showEmptyImg.value = false
      if (proxy?.topicPictureList?.length) {
        pictureList.value = proxy.topicPictureList
        cursorIndex.value = proxy.cursor
      }

      if (proxy?.isServerFetched && !proxy?.topicPictureList?.length) {
        showEmptyImg.value = true
      }

      if (!proxy?.isServerFetched) {
        getData(true)
      }
    })

    onActivated(() => {
      window?.addEventListener('scroll', handleScrollPosition)
    })

    onDeactivated(() => {
      window?.removeEventListener('scroll', handleScrollPosition)
    })

    const handleScrollPosition = rafThrottle(() => {
      const scrollTop = document.documentElement.scrollTop || 0;
      const scrollHeight = document.documentElement.scrollHeight || 0;
      const clientHeight = document.documentElement.clientHeight || 0;
      const distanceFromBottom = scrollHeight - clientHeight - scrollTop;

      if (distanceFromBottom <= 300) {
        getData();
      }
    })

    // Topic页面曝光埋点上报
    const sendPv = () => {
      const params = commonPvParams({ defaultPageSource: 'topic' })
      // 获取启动config
      const country = proxy.$store.state.startup.countryInfo
      const user_id = cookies.get('user_id')
      const topic_name = topicNameContent.value
      immediateSendLog({
        type: 'pv',
        name: 'MOBILE_SEO_TOPIC_PAGE',
        value: {
          country,
          user_id,
          topic_name,
          ...params
        }
      })
    }

    return {
      pictureList,
      getData,
      isLoading,
      showEmptyImg,
      cursorIndex,
      WebFeedType,
    }
  },
  async asyncData(ctx: any) {
    let seoData = []
    let isServerFetched = false
    let metaInfoData = []
    let topicPictureList: Array<TopicItem> = []
    let cursor: number | string = 0
    let title = getDefaultTitle(ctx)

    if(!ctx.route.query?.tabName && !ctx.route.params?.content) return ctx.redirect('/foryou');

    if (process.server) {
      try {
        setCommonParamsCookiesInServer({
          bucket: ctx.store.state.startup.bucket,
          host: ctx.req.headers.host,
          ctx,
          countryInfo: ctx.store.state.startup.countryInfo,
        })

        isServerFetched = true

        const topicName = ctx?.route?.params?.content || ''
        ctx.store.commit('setTopicName', topicName?.replace(/(-)/g, ' '));
        let pidStrList: Array<string> = []

        const pictureListRes = await TopicApi(ctx?.$axios).getTopicList({
          topic: encodeURIComponent(topicName?.trim()?.replace(/\s/g, '-')),
          pcursor: 0,
          count: 25,
        })

        if (pictureListRes?.result === ResponseBaseResult.RESULT_OK) {
          topicPictureList = pictureListRes?.feeds || []
          cursor = pictureListRes?.pcursor || '0'
          pidStrList = topicPictureList?.map((item) => {
            if (item?.web_feed_type === WebFeedType.PHOTO_CARD) {
              return (item as Feed)?.photo_id_str || ''
            } else if (item?.web_feed_type === WebFeedType.PICTURE_CARD) {
              return (item as Picture)?.pic_id_str || ''
            } else {
              return ''
            }
          }
          )?.filter(pid => pid !== '') || [];
        }

        const url = `https://${ctx?.req?.headers?.host}${ctx?.route?.path}` || ''

        // 获取google ldjson 标签
        let seoPromise = getLdJson({ ctx, params: { url, pidStrList: pidStrList || '' } })

        // 获取meta info
        let metaInfoPromise = getMetaInfo({ ctx, params: { url, pidStrList: pidStrList || '', locale: ctx.req.headers['accept-language'] } })

        let [seoRes, metaInfoRes] = await Promise.all([seoPromise, metaInfoPromise])

        if (seoRes.status === 200) {
          seoData = seoRes.data
        }

        if (metaInfoRes.status === 200) {
          metaInfoData = metaInfoRes.data
          title = extractTitleFromMetaInfo({ ctx, metaInfoData })
        }
      } catch (error) {
        console.log(error)
      }
    }

    return {
      topicPictureList,
      seoData,
      isServerFetched,
      metaInfoData,
      title,
      cursor,
    }
  },
  head() {
    const head = header(this) as any

    return {
      ...head,
    }
  },
})
